import {
  AVATAR_COLORS,
  activeClubs,
  MAX_ANNOUNCEMENT_LENGTH,
  MAX_CLUBS_PER_PLAYER,
  MAX_CLUB_BLURB,
  MAX_DM_LENGTH,
  SEARCH_MIN_LENGTH,
  getClub,
  type ClubAnnouncementDto,
  type ClubId,
  type ClubRequestDto,
  type ClubRosterDto,
  type FriendRelation,
  type FriendSummary,
  type PlayerBrief,
  type SearchResult,
} from "@clubap/shared";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { relationOf } from "../social/merge.js";
import type { SocialActions, SocialState } from "../social/useSocial.js";
import { ClubBadges, ClubMark } from "./ClubBadges.js";

interface Props {
  me: string;
  state: SocialState;
  actions: SocialActions;
  /**
   * El que mira es admin. Enseña los controles de nombrar presidente.
   *
   * Es sólo comodidad: el endpoint vuelve a comprobarlo con `puedeNombrarPresidente`.
   * Un cliente modificado no lee este archivo, así que esconder un botón nunca es la
   * defensa — la defensa está en el servidor y esto le ahorra el viaje a quien no puede.
   */
  canAppoint: boolean;
  onClose: () => void;
}
type Tab = "amigos" | "solicitudes" | "buscar" | "clubes";

const TABS: Array<{ id: Tab; label: string }> = [
  { id: "amigos", label: "Amigos" },
  { id: "solicitudes", label: "Solicitudes" },
  { id: "buscar", label: "Buscar" },
  { id: "clubes", label: "Clubes" },
];

function swatch(colorIndex: number): string {
  const color = AVATAR_COLORS[colorIndex] ?? AVATAR_COLORS[0];
  return `#${color.toString(16).padStart(6, "0")}`;
}

function timeOf(at: number): string {
  return new Date(at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/** Punto de presencia. Es lo único que se sabe del otro: conectado o no. */
function Presence({ online }: { online: boolean }) {
  return (
    <span
      className={`presence${online ? " presence--on" : ""}`}
      title={online ? "Conectado" : "Desconectado"}
      aria-label={online ? "Conectado" : "Desconectado"}
    />
  );
}

function PersonRow({
  person,
  children,
  onClick,
}: {
  person: PlayerBrief;
  children?: ReactNode;
  onClick?: () => void;
}) {
  const body = (
    <>
      <span className="social__dot" style={{ background: swatch(person.color) }} />
      <Presence online={person.online} />
      <span className="social__name">{person.username}</span>
      <ClubBadges clubs={person.clubs} />
    </>
  );

  return (
    <li className="social__row">
      {onClick ? (
        <button className="social__pick" type="button" onClick={onClick}>
          {body}
        </button>
      ) : (
        <span className="social__pick social__pick--static">{body}</span>
      )}
      {children}
    </li>
  );
}

/**
 * La libreta social: amigos, solicitudes, buscador y clubes, más la conversación.
 *
 * Es un panel aparte del chat de sala a propósito. El chat es del sitio donde estás
 * —se va al cruzar una puerta— y esto es de tu cuenta: mezclarlos haría que un
 * mensaje privado se leyera como algo que dijo alguien en voz alta.
 */
export function SocialPanel({ me, state, actions, canAppoint, onClose }: Props) {
  const [tab, setTab] = useState<Tab>("amigos");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [roster, setRoster] = useState<ClubRosterDto | null>(null);
  const [draft, setDraft] = useState("");

  const logRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // Escape cierra primero la conversación: es la capa de encima.
      if (state.activePeer) actions.closeThread();
      else onClose();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [actions, onClose, state.activePeer]);

  const peer = state.activePeer;
  const thread = peer ? state.threads[peer] : undefined;
  const friend = peer ? state.friends.find((entry) => entry.id === peer) : undefined;

  // Al fondo con cada mensaje nuevo, como el chat de sala.
  useEffect(() => {
    const log = logRef.current;
    if (log) log.scrollTop = log.scrollHeight;
  }, [thread?.messages.length, peer]);

  useEffect(() => {
    setDraft("");
  }, [peer]);

  const runSearch = useCallback(async () => {
    const text = query.trim();
    if (text.length < SEARCH_MIN_LENGTH) return;

    setSearching(true);
    setResults(await actions.search(text));
    setSearching(false);
  }, [actions, query]);

  const openRoster = useCallback(
    async (clubId: string) => {
      setRoster(await actions.roster(clubId));
    },
    [actions],
  );

  const pending = state.incoming.length;

  return (
    <div className="overlay">
      <div className="panel sheet social">
        <div className="sheet__head">
          <h2 className="sheet__title">Amigos y clubes</h2>
          {!state.connected && (
            <span className="social__offline" title="Sin avisos en vivo; los datos siguen al día">
              sin conexión
            </span>
          )}
          <button className="icon-btn" type="button" onClick={onClose} aria-label="Cerrar panel social">
            ×
          </button>
        </div>

        {state.error && (
          <p className="sheet__error" role="alert">
            {state.error}
          </p>
        )}

        {peer && friend ? (
          <section className="social__thread">
            <div className="social__threadHead">
              <button className="btn btn--small" type="button" onClick={actions.closeThread}>
                ‹ Volver
              </button>
              <Presence online={friend.online} />
              <span className="social__name">{friend.username}</span>
              <ClubBadges clubs={friend.clubs} />
            </div>

            <div className="social__log" ref={logRef}>
              {thread?.nextCursor && (
                <button
                  className="btn btn--small social__more"
                  type="button"
                  disabled={thread.loading}
                  onClick={() => void actions.loadOlder(peer)}
                >
                  {thread.loading ? "…" : "Ver mensajes anteriores"}
                </button>
              )}

              {!thread && <p className="sheet__hint">Cargando…</p>}

              {thread && thread.messages.length === 0 && (
                <p className="sheet__hint">Todavía no se han escrito nada.</p>
              )}

              {thread?.messages.map((message) => {
                const mine = message.from === me;

                return (
                  <p
                    className={`chat__line${mine ? " chat__line--mine" : ""}`}
                    key={message.id}
                    title={timeOf(message.at)}
                  >
                    <span className="chat__who">{mine ? "tú" : friend.username}</span>
                    <span className="chat__text">{message.text}</span>
                  </p>
                );
              })}
            </div>

            <form
              className="chat__form"
              onSubmit={(event) => {
                event.preventDefault();
                const text = draft.trim();
                if (!text) return;
                setDraft("");
                void actions.send(peer, text);
              }}
            >
              <input
                className="chat__input"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="Escríbele…"
                maxLength={MAX_DM_LENGTH}
                aria-label={`Mensaje para ${friend.username}`}
              />
              <button
                className="chat__send"
                type="submit"
                disabled={draft.trim().length === 0}
                aria-label="Enviar mensaje"
              >
                ➤
              </button>
            </form>
          </section>
        ) : (
          <>
            <div className="auth__tabs social__tabs">
              {TABS.map((entry) => (
                <button
                  className={`auth__tab${tab === entry.id ? " auth__tab--on" : ""}`}
                  key={entry.id}
                  type="button"
                  onClick={() => {
                    setTab(entry.id);
                    setRoster(null);
                  }}
                >
                  {entry.label}
                  {entry.id === "solicitudes" && pending > 0 && (
                    <span className="sheet__badge social__count">{pending}</span>
                  )}
                </button>
              ))}
            </div>

            {tab === "amigos" && <Friends state={state} actions={actions} />}

            {tab === "solicitudes" && <Requests state={state} actions={actions} />}

            {tab === "buscar" && (
              <section className="sheet__group">
                <form
                  className="chat__form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void runSearch();
                  }}
                >
                  <input
                    className="chat__input"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Nombre de usuario…"
                    aria-label="Buscar por nombre de usuario"
                  />
                  <button
                    className="chat__send"
                    type="submit"
                    disabled={query.trim().length < SEARCH_MIN_LENGTH}
                    aria-label="Buscar"
                  >
                    ⌕
                  </button>
                </form>

                {searching && <p className="sheet__hint">Buscando…</p>}

                {!searching && results?.length === 0 && (
                  <p className="sheet__hint">Nadie con ese nombre.</p>
                )}

                {!searching && results && results.length > 0 && (
                  <ul className="sheet__list">
                    {results.map((result) => (
                      <PersonRow person={result} key={result.id}>
                        <PersonAction
                          personId={result.id}
                          me={me}
                          state={state}
                          actions={actions}
                          fallback={result.relation}
                        />
                      </PersonRow>
                    ))}
                  </ul>
                )}

                {!results && (
                  <p className="sheet__hint">
                    Busca a alguien por su nombre de usuario para agregarlo.
                  </p>
                )}
              </section>
            )}

            {tab === "clubes" && (
              <Clubs
                me={me}
                state={state}
                actions={actions}
                roster={roster}
                canAppoint={canAppoint}
                onRoster={openRoster}
                onRosterChanged={setRoster}
                onCloseRoster={() => setRoster(null)}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Qué se puede hacer con esta persona.
 *
 * La relación se DERIVA del estado vigente y no del campo que trajo la búsqueda: ese
 * campo se queda viejo en cuanto le mandas la solicitud, y el botón "Agregar" seguía
 * ahí invitando a repetirla. El de la respuesta sólo se usa como respaldo, por si el
 * estado local todavía no conoce esa relación.
 */
function PersonAction({
  personId,
  me,
  state,
  actions,
  fallback,
}: {
  personId: string;
  me: string;
  state: SocialState;
  actions: SocialActions;
  fallback?: FriendRelation;
}) {
  /*
   * El respaldo sólo vale ANTES de tener la foto de la base.
   *
   * Con `ready`, el estado local ya conoce todas tus relaciones, así que "ninguna" es
   * una respuesta y no una ignorancia: seguir prefiriendo el campo que trajo la búsqueda
   * dejaría "enviada" para siempre en una solicitud que acabas de cancelar.
   */
  const derived = relationOf(state, me, personId);
  const relation = derived === "none" && !state.ready ? (fallback ?? "none") : derived;

  if (relation === "self") return <span className="sheet__badge">tú</span>;
  if (relation === "friend") return <span className="sheet__badge">amigo</span>;
  if (relation === "request_sent") return <span className="social__muted">enviada</span>;
  if (relation === "request_received") return <span className="social__muted">te escribió</span>;

  return (
    <button className="btn btn--small" type="button" onClick={() => void actions.request(personId)}>
      Agregar
    </button>
  );
}

function Friends({ state, actions }: { state: SocialState; actions: SocialActions }) {
  if (!state.ready) return <p className="sheet__hint">Cargando…</p>;

  if (state.friends.length === 0) {
    return (
      <div className="sheet__empty">
        <p>Todavía no tienes amigos agregados.</p>
        <p className="sheet__hint">
          Toca a alguien en la sala, o búscalo por su nombre de usuario.
        </p>
      </div>
    );
  }

  return (
    <ul className="sheet__list">
      {state.friends.map((friend: FriendSummary) => (
        <PersonRow person={friend} key={friend.id} onClick={() => void actions.openThread(friend.id)}>
          {friend.unread > 0 && <span className="sheet__badge">{friend.unread}</span>}
          <button
            className="social__quiet"
            type="button"
            title={`Eliminar a ${friend.username} de tus amigos`}
            onClick={() => void actions.unfriend(friend.id)}
          >
            ×
          </button>
        </PersonRow>
      ))}
    </ul>
  );
}

function Requests({ state, actions }: { state: SocialState; actions: SocialActions }) {
  if (state.incoming.length === 0 && state.outgoing.length === 0) {
    return <p className="sheet__hint">No hay solicitudes pendientes.</p>;
  }

  return (
    <>
      {state.incoming.length > 0 && (
        <section className="sheet__group">
          <h3 className="sheet__slot">Te escribieron</h3>
          <ul className="sheet__list">
            {state.incoming.map((entry) => (
              <PersonRow person={entry.from} key={entry.id}>
                <button
                  className="btn btn--small"
                  type="button"
                  onClick={() => void actions.accept(entry.id)}
                >
                  Aceptar
                </button>
                <button
                  className="social__quiet"
                  type="button"
                  title="Rechazar"
                  onClick={() => void actions.reject(entry.id)}
                >
                  ×
                </button>
              </PersonRow>
            ))}
          </ul>
        </section>
      )}

      {state.outgoing.length > 0 && (
        <section className="sheet__group">
          <h3 className="sheet__slot">Enviadas</h3>
          <ul className="sheet__list">
            {state.outgoing.map((entry) => (
              <PersonRow person={entry.to} key={entry.id}>
                <button
                  className="social__quiet"
                  type="button"
                  title="Cancelar solicitud"
                  onClick={() => void actions.cancel(entry.id)}
                >
                  ×
                </button>
              </PersonRow>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

/**
 * El escritorio de un club: solicitudes por responder, descripción y tablón.
 *
 * Lo que se ve depende de quién eres, y los tres bloques son distintos:
 * · Las solicitudes SÓLO las ve quien preside — quién quiere entrar no es asunto de
 *   los miembros.
 * · El tablón lo leen todos los miembros y sólo escribe el presidente.
 * · La descripción la lee cualquiera desde la lista; acá sólo aparece el editor.
 *
 * Pide sus datos al abrirse y no los guarda en el hook, porque son de UN club: en el
 * estado social —que es de la cuenta entera— habría que invalidarlos con cada aviso
 * de cualquier otro club.
 */
function ClubDesk({
  clubId,
  preside,
  member,
  actions,
  onRosterChanged,
}: {
  clubId: ClubId;
  preside: boolean;
  member: boolean;
  actions: SocialActions;
  onRosterChanged: (roster: ClubRosterDto) => void;
}) {
  const [requests, setRequests] = useState<ClubRequestDto[] | null>(null);
  const [board, setBoard] = useState<ClubAnnouncementDto[] | null>(null);
  const [draft, setDraft] = useState("");
  const [blurb, setBlurb] = useState("");
  const [editing, setEditing] = useState(false);

  /*
   * Depende de `clubId` y de los dos permisos, no del objeto de acciones.
   *
   * `actions` ya es estable —el hook lo memoriza— pero listarlo igual sería invitar a
   * que un día deje de serlo y esto se recargue en cada render. Lo que de verdad
   * cambia lo que hay que pedir es de qué club se trata y qué puedo ver de él.
   */
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      if (preside) {
        const found = await actions.clubRequests(clubId);
        if (!cancelled && found) setRequests(found.requests);
      } else if (!cancelled) {
        setRequests(null);
      }

      if (member) {
        const found = await actions.announcements(clubId);
        if (!cancelled && found) setBoard(found.announcements);
      } else if (!cancelled) {
        setBoard(null);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId, preside, member]);

  return (
    <>
      {preside && requests !== null && (
        <section className="sheet__group">
          <h3 className="sheet__slot">Solicitudes ({requests.length})</h3>

          {requests.length === 0 ? (
            <p className="sheet__hint">Nadie está esperando.</p>
          ) : (
            <ul className="sheet__list">
              {requests.map((entry) => (
                <li className="social__row" key={entry.id}>
                  <span className="social__pick social__pick--static">
                    <span
                      className="social__dot"
                      style={{ background: swatch(entry.from.color) }}
                    />
                    <span className="social__name">{entry.from.username}</span>
                  </span>

                  <button
                    className="btn btn--small"
                    type="button"
                    onClick={() => {
                      void actions
                        .resolveClubRequest(clubId, entry.id, "accept")
                        .then((result) => result && setRequests(result.requests));
                    }}
                  >
                    Aceptar
                  </button>

                  <button
                    className="social__quiet"
                    type="button"
                    onClick={() => {
                      void actions
                        .resolveClubRequest(clubId, entry.id, "reject")
                        .then((result) => result && setRequests(result.requests));
                    }}
                  >
                    Rechazar
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {preside && (
        <section className="sheet__group">
          <h3 className="sheet__slot">Descripción</h3>

          {editing ? (
            <div className="editor__row">
              <input
                className="join__input"
                value={blurb}
                maxLength={MAX_CLUB_BLURB}
                placeholder="De qué va este club…"
                onChange={(event) => setBlurb(event.target.value)}
              />
              <button
                className="btn btn--small"
                type="button"
                disabled={blurb.trim().length === 0}
                onClick={() => {
                  void actions.setBlurb(clubId, blurb).then((result) => {
                    if (result) setEditing(false);
                  });
                }}
              >
                Guardar
              </button>
              <button className="social__quiet" type="button" onClick={() => setEditing(false)}>
                Cancelar
              </button>
            </div>
          ) : (
            <button className="social__quiet" type="button" onClick={() => setEditing(true)}>
              Cambiar la descripción
            </button>
          )}
        </section>
      )}

      {member && board !== null && (
        <section className="sheet__group">
          <h3 className="sheet__slot">Tablón</h3>

          {preside && (
            <div className="editor__row">
              <input
                className="join__input"
                value={draft}
                maxLength={MAX_ANNOUNCEMENT_LENGTH}
                placeholder="Anunciar algo al club…"
                onChange={(event) => setDraft(event.target.value)}
              />
              <button
                className="btn btn--small"
                type="button"
                disabled={draft.trim().length === 0}
                onClick={() => {
                  void actions.announce(clubId, draft).then((result) => {
                    if (result) {
                      setBoard(result.announcements);
                      setDraft("");
                    }
                  });
                }}
              >
                Publicar
              </button>
            </div>
          )}

          {board.length === 0 ? (
            <p className="sheet__hint">Todavía no hay anuncios.</p>
          ) : (
            <div className="social__log">
              {board.map((entry) => (
                <p className="chat__line" key={entry.id}>
                  {/* Sin firma cuando el autor borró su cuenta: el anuncio le pasó al
                      club y sigue valiendo, lo único que se pierde es de quién era. */}
                  <span className="chat__who">{entry.author?.username ?? "—"}</span>
                  {entry.body}
                </p>
              ))}
            </div>
          )}
        </section>
      )}
    </>
  );
}

function Clubs({
  me,
  state,
  actions,
  roster,
  canAppoint,
  onRoster,
  onRosterChanged,
  onCloseRoster,
}: {
  me: string;
  state: SocialState;
  actions: SocialActions;
  roster: ClubRosterDto | null;
  /** El que mira es admin: puede nombrar y destituir presidentes. */
  canAppoint: boolean;
  onRoster: (clubId: string) => void;
  /** El servidor devolvió un roster nuevo tras nombrar: se pinta ése. */
  onRosterChanged: (roster: ClubRosterDto) => void;
  onCloseRoster: () => void;
}) {
  const mine = state.clubs.filter((club) => club.joined).length;

  if (roster) {
    const club = getClub(roster.clubId);
    /* Se deriva del ROSTER que acaba de llegar y no del resumen: el resumen puede ser
       de hace un momento, y quien acaba de ser nombrado tiene que ver sus botones ya. */
    const preside = roster.members.some((entry) => entry.id === me && entry.role === "presidente");
    const member = roster.members.some((entry) => entry.id === me);

    return (
      <section className="sheet__group">
        <div className="social__threadHead">
          <button className="btn btn--small" type="button" onClick={onCloseRoster}>
            ‹ Volver
          </button>
          <span className="social__name">{club?.name ?? roster.clubId}</span>
        </div>

        {roster.members.length === 0 ? (
          <p className="sheet__hint">Nadie se ha unido todavía.</p>
        ) : (
          <ul className="sheet__list">
            {roster.members.map((member) => (
              <PersonRow person={member} key={member.id}>
                {member.role === "presidente" && (
                  <span className="sheet__badge" title="Preside este club">
                    Preside
                  </span>
                )}

                {/*
                  Nombrar sale sólo para un admin, y aun así el servidor lo vuelve a
                  comprobar: esconder el botón es comodidad, no seguridad — un cliente
                  modificado no ve este archivo.
                */}
                {canAppoint && (
                  <button
                    className="social__quiet"
                    type="button"
                    title={
                      member.role === "presidente"
                        ? "Dejar el club sin presidente"
                        : `Nombrar a ${member.username} presidente`
                    }
                    onClick={() => {
                      void actions
                        .setPresident(
                          roster.clubId,
                          member.role === "presidente" ? null : member.id,
                        )
                        .then((result) => {
                          if (result) onRosterChanged(result.roster);
                        });
                    }}
                  >
                    {member.role === "presidente" ? "Destituir" : "Nombrar"}
                  </button>
                )}

                {/* Expulsar sólo lo ve quien preside, y nunca sobre sí mismo: para
                    irse está "Salir", que es lo que la persona quiere de verdad. */}
                {preside && member.id !== me && (
                  <button
                    className="social__quiet"
                    type="button"
                    title={`Sacar a ${member.username} del club`}
                    onClick={() => {
                      void actions.expel(roster.clubId, member.id).then((result) => {
                        if (result) onRosterChanged(result.roster);
                      });
                    }}
                  >
                    Expulsar
                  </button>
                )}

                <PersonAction personId={member.id} me={me} state={state} actions={actions} />
              </PersonRow>
            ))}
          </ul>
        )}

        <ClubDesk
          clubId={roster.clubId}
          preside={preside}
          member={member}
          actions={actions}
          onRosterChanged={onRosterChanged}
        />

        <p className="sheet__hint">
          La presencia es la de este momento: se actualiza al volver a abrir el roster.
        </p>
      </section>
    );
  }

  return (
    <section className="sheet__group">
      <ul className="sheet__list">
        {activeClubs().map((club) => {
          const summary = state.clubs.find((entry) => entry.id === club.id);
          const joined = summary?.joined === true;
          /* Solicitud mía esperando: el botón deja de ser "pedir" y pasa a "cancelar". */
          const pedida = summary?.requested === true;

          return (
            <li className="sheet__item" key={club.id}>
              <span className="badge" style={{ color: club.color, borderColor: club.color }}>
                <ClubMark club={club} />
              </span>

              <span className="sheet__name">
                {club.name}
                {/* La descripción sale del RESUMEN y no del catálogo: si el presidente
                    la cambió, la del catálogo es la vieja. El servidor ya resolvió
                    cuál de las dos vale. */}
                <span className="social__blurb">{summary?.blurb ?? club.blurb}</span>
                {summary?.president && (
                  <span className="social__muted">Preside {summary.president.username}</span>
                )}
              </span>

              <button
                className="social__quiet"
                type="button"
                title={
                  summary && summary.pending > 0
                    ? `${summary.pending} solicitud(es) por responder`
                    : "Ver quién está"
                }
                onClick={() => onRoster(club.id)}
              >
                ◍ {summary?.members ?? 0}
                {summary != null && summary.pending > 0 && (
                  <span className="sheet__badge">{summary.pending}</span>
                )}
              </button>

              <button
                className="btn btn--small"
                type="button"
                disabled={!joined && !pedida && mine >= MAX_CLUBS_PER_PLAYER}
                title={
                  joined
                    ? "Salir del club"
                    : pedida
                      ? "Retirar la solicitud"
                      : mine >= MAX_CLUBS_PER_PLAYER
                        ? `Ya estás en ${MAX_CLUBS_PER_PLAYER} clubes`
                        : summary?.president
                          ? "Pedir entrada: la responde quien lo preside"
                          : "Este club no tiene presidente, se entra directo"
                }
                onClick={() =>
                  void (joined
                    ? actions.leaveClub(club.id)
                    : pedida
                      ? actions.cancelClubRequest(club.id)
                      : actions.joinClub(club.id))
                }
              >
                {joined ? "Salir" : pedida ? "Cancelar" : summary?.president ? "Pedir" : "Unirme"}
              </button>
            </li>
          );
        })}
      </ul>

      <p className="sheet__hint">
        Puedes estar en {MAX_CLUBS_PER_PLAYER} clubes a la vez. La insignia se ve en tu perfil y
        en la sala.
      </p>
    </section>
  );
}
